<script>
import constants from '@/constants';
import { axiosRequest, METHOD } from '@/api/default.js';

export default {
    name: 'ProviderActionPopup',

    props: {
        // 'delete' | 'migrate'
        mode: {
            type: String,
            required: true,
            validator: (v) => ['delete', 'migrate'].includes(v),
        },
        publisher: {
            type: Object,
            required: true,
        },
    },

    emits: ['close'],

    data() {
        return {
            isConfirming: false,

            // Migrate mode state
            providerSearch: '',
            providerSearchTimer: null,
            providerResults: [],
            providerSearchLoading: false,
            selectedProvider: null,
        };
    },

    computed: {
        isDeleteMode() {
            return this.mode === 'delete';
        },
        isMigrateMode() {
            return this.mode === 'migrate';
        },
        canConfirmMigrate() {
            return this.selectedProvider !== null;
        },
    },

    methods: {
        // ── Provider search (migrate mode) ───────────────────────────────────
        handleProviderSearchInput() {
            clearTimeout(this.providerSearchTimer);
            this.selectedProvider = null;
            if (!this.providerSearch.trim()) {
                this.providerResults = [];
                return;
            }
            this.providerSearchTimer = setTimeout(() => {
                this.searchProviders(this.providerSearch.trim());
            }, 300);
        },

        async searchProviders(query) {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderActionPopup.searchProviders] searching...'
                );
            }
            this.providerSearchLoading = true;
            try {
                const results = await axiosRequest({
                    url: constants.URL.PROVIDER_GET,
                    method: METHOD.GET,
                    requestName: 'GET PROVIDER SEARCH',
                    isLoader: false,
                    params: {
                        search: query,
                        get_all: true,
                    },
                });

                // Filter out the current publisher from results to prevent
                // migrating to self
                const raw = Array.isArray(results) ? results : (results?.items || []);
                this.providerResults = raw.filter(
                    (p) => p.id !== this.publisher.id
                );

                if (process.env.NODE_ENV !== 'production') {
                    console.info(
                        '[ProviderActionPopup.searchProviders] results count:',
                        this.providerResults.length
                    );
                }
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[ProviderActionPopup.searchProviders] Error:',
                        err?.message
                    );
                }
                this.providerResults = [];
            } finally {
                this.providerSearchLoading = false;
            }
        },

        selectProvider(provider) {
            this.selectedProvider = provider;
            this.providerSearch = provider.name || provider.email || String(provider.id);
            this.providerResults = [];
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderActionPopup.selectProvider] Selected target provider:',
                    { id: provider.id }
                );
            }
        },

        clearProviderSelection() {
            this.selectedProvider = null;
            this.providerSearch = '';
            this.providerResults = [];
        },

        // ── Actions ──────────────────────────────────────────────────────────
        async handleConfirm() {
            if (this.isConfirming) return;
            this.isConfirming = true;

            try {
                if (this.isDeleteMode) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.info(
                            '[ProviderActionPopup.handleConfirm] Deleting publisher:',
                            this.publisher.id
                        );
                    }
                    await this.$store.dispatch(
                        'deleteAdminPublisher',
                        this.publisher.id
                    );
                } else if (this.isMigrateMode) {
                    if (!this.selectedProvider) return;
                    if (process.env.NODE_ENV !== 'production') {
                        console.info(
                            '[ProviderActionPopup.handleConfirm] Migrating publisher:',
                            {
                                publisherId: this.publisher.id,
                                targetProviderId: this.selectedProvider.id,
                            }
                        );
                    }
                    await this.$store.dispatch('migrateAdminPublisher', {
                        publisherId: this.publisher.id,
                        targetProviderId: this.selectedProvider.id,
                    });
                }
                this.$emit('close');
            } catch (err) {
                // Error popup is handled in the Vuex actions
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[ProviderActionPopup.handleConfirm] Error:',
                        err?.message
                    );
                }
            } finally {
                this.isConfirming = false;
            }
        },

        handleCancel() {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderActionPopup.handleCancel] Cancelled for publisher:',
                    this.publisher.id
                );
            }
            this.$emit('close');
        },

        handleOverlayClick(event) {
            if (event.target === event.currentTarget) {
                this.handleCancel();
            }
        },
    },
};
</script>

<template>
    <div class="modal_overlay" @click="handleOverlayClick">
        <div class="modal_panel" role="dialog" aria-modal="true">
            <!-- Header -->
            <div class="modal_header">
                <h3 class="modal_title">
                    <span v-if="isDeleteMode">Delete Publisher</span>
                    <span v-else>Migrate Publisher</span>
                </h3>
                <button class="close_btn" @click="handleCancel" title="Close">
                    ✕
                </button>
            </div>

            <!-- Body -->
            <div class="modal_body">
                <!-- Publisher info banner -->
                <div class="publisher_info">
                    <span class="publisher_label">Publisher:</span>
                    <span class="publisher_value">
                        {{ publisher.name || publisher.email || publisher.id }}
                    </span>
                    <span v-if="publisher.email" class="publisher_email">
                        {{ publisher.email }}
                    </span>
                </div>

                <!-- DELETE mode -->
                <template v-if="isDeleteMode">
                    <div class="warning_block">
                        <p class="warning_text">
                            Are you sure you want to
                            <strong>permanently delete</strong> this publisher?
                        </p>
                        <p class="warning_subtext">
                            This action cannot be undone. All publisher data will be removed.
                        </p>
                    </div>
                </template>

                <!-- MIGRATE mode -->
                <template v-else>
                    <div class="migrate_description">
                        <p>
                            Select the target provider to migrate this publisher's
                            data to. All associated files and settings will be
                            transferred.
                        </p>
                    </div>

                    <div class="field_col">
                        <label class="field_label">
                            Target Provider
                            <span class="field_required">*</span>
                        </label>

                        <!-- Search input -->
                        <div class="search_wrapper">
                            <input
                                v-model="providerSearch"
                                @input="handleProviderSearchInput"
                                type="text"
                                class="provider_search_input"
                                placeholder="Search providers by name..."
                                autocomplete="off"
                            />
                            <button
                                v-if="selectedProvider || providerSearch"
                                @click="clearProviderSelection"
                                class="clear_provider_btn"
                                title="Clear"
                            >
                                ✕
                            </button>
                        </div>

                        <!-- Loading -->
                        <div
                            v-if="providerSearchLoading"
                            class="search_state"
                        >
                            Searching...
                        </div>

                        <!-- Results dropdown -->
                        <div
                            v-else-if="providerResults.length > 0"
                            class="provider_results"
                        >
                            <div
                                v-for="provider in providerResults"
                                :key="provider.id"
                                class="provider_result_item"
                                @click="selectProvider(provider)"
                            >
                                <span class="result_name">
                                    {{ provider.name || provider.email || provider.id }}
                                </span>
                                <span
                                    v-if="provider.email"
                                    class="result_email"
                                >
                                    {{ provider.email }}
                                </span>
                            </div>
                        </div>

                        <!-- No results -->
                        <div
                            v-else-if="
                                providerSearch.trim() &&
                                !providerSearchLoading &&
                                !selectedProvider
                            "
                            class="search_state search_no_results"
                        >
                            No providers found.
                        </div>

                        <!-- Selected provider badge -->
                        <div v-if="selectedProvider" class="selected_badge">
                            <span class="selected_label">Selected:</span>
                            <span class="selected_name">
                                {{
                                    selectedProvider.name ||
                                    selectedProvider.email ||
                                    selectedProvider.id
                                }}
                            </span>
                        </div>
                    </div>
                </template>
            </div>

            <!-- Actions -->
            <div class="modal_actions">
                <button
                    class="btn_cancel"
                    @click="handleCancel"
                    :disabled="isConfirming"
                >
                    Cancel
                </button>

                <!-- DELETE confirm button (danger) -->
                <button
                    v-if="isDeleteMode"
                    class="btn_danger"
                    @click="handleConfirm"
                    :disabled="isConfirming"
                >
                    <span v-if="isConfirming">Deleting...</span>
                    <span v-else>Delete</span>
                </button>

                <!-- MIGRATE confirm button -->
                <button
                    v-else
                    class="btn_primary"
                    @click="handleConfirm"
                    :disabled="isConfirming || !canConfirmMigrate"
                >
                    <span v-if="isConfirming">Migrating...</span>
                    <span v-else>Migrate</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal_overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal_panel {
    background: var(--background-color);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    width: 500px;
    max-width: 96vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
    display: flex;
    flex-direction: column;
}

/* Header */
.modal_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px 14px;
    border-bottom: 1px solid var(--border-color);
    position: sticky;
    top: 0;
    background: var(--background-color);
    z-index: 1;
}

.modal_title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--text-color);
}

.close_btn {
    background: none;
    border: none;
    color: var(--text-color-secondary);
    font-size: 18px;
    cursor: pointer;
    padding: 4px 6px;
    line-height: 1;
    border-radius: 4px;
    transition: color 0.2s;
}

.close_btn:hover {
    color: var(--text-color);
}

/* Body */
.modal_body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Publisher info banner */
.publisher_info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 14px;
    background: var(--background-secondary);
    border-radius: 6px;
    border: 1px solid var(--border-color);
}

.publisher_label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.publisher_value {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color);
}

.publisher_email {
    font-size: 12px;
    color: var(--text-color-secondary);
    font-family: monospace;
}

/* Warning (delete mode) */
.warning_block {
    padding: 14px 16px;
    border-radius: 6px;
    background: #fee2e2;
    border: 1px solid #fca5a5;
}

.warning_text {
    margin: 0 0 6px;
    font-size: 14px;
    color: #991b1b;
}

.warning_subtext {
    margin: 0;
    font-size: 12px;
    color: #b91c1c;
}

/* Migrate description */
.migrate_description p {
    margin: 0;
    font-size: 14px;
    color: var(--text-color-secondary);
    line-height: 1.5;
}

/* Fields */
.field_col {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field_label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-color-secondary);
}

.field_required {
    color: #ef4444;
    margin-left: 2px;
}

/* Provider search */
.search_wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.provider_search_input {
    width: 100%;
    padding: 9px 36px 9px 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    transition: border-color 0.2s;
    box-sizing: border-box;
}

.provider_search_input:focus {
    outline: none;
    border-color: var(--primary-color);
}

.clear_provider_btn {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: var(--text-color-secondary);
    cursor: pointer;
    font-size: 14px;
    padding: 4px;
    line-height: 1;
}

.clear_provider_btn:hover {
    color: var(--text-color);
}

/* Results */
.provider_results {
    border: 1px solid var(--border-color);
    border-radius: 6px;
    overflow: hidden;
    max-height: 200px;
    overflow-y: auto;
}

.provider_result_item {
    display: flex;
    flex-direction: column;
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid var(--border-color);
    transition: background 0.15s;
}

.provider_result_item:last-child {
    border-bottom: none;
}

.provider_result_item:hover {
    background: var(--background-hover);
}

.result_name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-color);
}

.result_email {
    font-size: 11px;
    color: var(--text-color-secondary);
    font-family: monospace;
    margin-top: 2px;
}

/* State messages */
.search_state {
    padding: 8px 12px;
    font-size: 13px;
    color: var(--text-color-secondary);
}

.search_no_results {
    font-style: italic;
}

/* Selected badge */
.selected_badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #d1fae5;
    border: 1px solid #6ee7b7;
    border-radius: 6px;
}

.selected_label {
    font-size: 12px;
    font-weight: 600;
    color: #065f46;
}

.selected_name {
    font-size: 13px;
    color: #065f46;
    font-weight: 500;
}

/* Actions */
.modal_actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px 18px;
    border-top: 1px solid var(--border-color);
}

.btn_cancel {
    padding: 9px 20px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: transparent;
    color: var(--text-color-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn_cancel:hover:not(:disabled) {
    border-color: var(--text-color);
    color: var(--text-color);
}

.btn_danger {
    padding: 9px 24px;
    border: none;
    border-radius: 6px;
    background: #ef4444;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
}

.btn_danger:hover:not(:disabled) {
    opacity: 0.88;
}

.btn_primary {
    padding: 9px 24px;
    border: none;
    border-radius: 6px;
    background: var(--main-color, #5b73e8);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
}

.btn_primary:hover:not(:disabled) {
    opacity: 0.9;
}

.btn_danger:disabled,
.btn_primary:disabled,
.btn_cancel:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
